import React, { useContext, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig from "../config/siteConfig";
import "../css/checkout.css";

const STANDARD_SHIPPING = Number(siteConfig.shippingRate) || 9.99;
const PROMO_CODE = "WELCOME10";
const PROMO_RATE = 0.1;
const DELIVERY_COUNTRIES = [
  ["US", "United States"],
  ["CA", "Canada"],
  ["GB", "United Kingdom"],
  ["AU", "Australia"],
];

const money = new Intl.NumberFormat(siteConfig.locale || "en-US", {
  style: "currency",
  currency: siteConfig.currency || "USD",
});

const parsePrice = (price) => {
  if (typeof price === "number") return Number.isFinite(price) ? price : 0;
  const parsed = Number(String(price ?? "").replace(/[^0-9.-]/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

const getImage = (product) => {
  if (product?.image) return product.image;
  if (Array.isArray(product?.images)) {
    const firstImage = product.images[0];
    return typeof firstImage === "string" ? firstImage : firstImage?.src;
  }
  return "";
};

const getProductMeta = (item, product) =>
  [
    item.size,
    item.color,
    product.weight,
    product.serves,
    product.attributes?.selection,
  ]
    .filter(Boolean)
    .filter((value, index, all) => all.indexOf(value) === index)
    .slice(0, 3)
    .join(" · ");

const initialForm = {
  email: "",
  firstName: "",
  lastName: "",
  company: "",
  address: "",
  apartment: "",
  city: "",
  region: "",
  postalCode: "",
  country: "US",
};

const CheckOut = () => {
  const cart = useContext(CartContext);
  const cartItems = cart?.cartItems || [];
  const fieldRefs = useRef({});
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [promoInput, setPromoInput] = useState("");
  const [appliedCode, setAppliedCode] = useState("");
  const [promoMessage, setPromoMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const getProductById = (id) =>
    productData.find((product) => String(product.id) === String(id));

  const lineItems = cartItems
    .map((item) => {
      const product = getProductById(item.id);
      if (!product) return null;
      const quantity = Math.max(1, Number(item.quantity) || 1);
      return {
        item,
        product,
        quantity,
        lineTotal: parsePrice(product.price) * quantity,
      };
    })
    .filter(Boolean);

  const subtotal = lineItems.reduce((sum, line) => sum + line.lineTotal, 0);
  const shipping = STANDARD_SHIPPING;
  const discount = appliedCode === PROMO_CODE ? subtotal * PROMO_RATE : 0;
  const total = Math.max(0, subtotal - discount + shipping);

  const updateField = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    if (errors[name]) {
      setErrors((current) => ({ ...current, [name]: "" }));
    }
  };

  const applyPromo = () => {
    const normalizedCode = promoInput.trim().toUpperCase();
    if (!normalizedCode) {
      setPromoMessage("Enter a promo code first.");
      return;
    }
    if (normalizedCode !== PROMO_CODE) {
      setAppliedCode("");
      setPromoMessage("That code is not available. Try WELCOME10.");
      return;
    }
    setPromoInput(PROMO_CODE);
    setAppliedCode(PROMO_CODE);
    setPromoMessage("WELCOME10 applied — you saved 10%.");
  };

  const removePromo = () => {
    setAppliedCode("");
    setPromoInput("");
    setPromoMessage("Promo code removed.");
  };

  const validate = () => {
    const nextErrors = {};
    const email = form.email.trim();

    if (!email) {
      nextErrors.email = "Enter an email address.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      nextErrors.email = "Enter a valid email address.";
    }
    if (!form.firstName.trim()) nextErrors.firstName = "Enter a first name.";
    if (!form.lastName.trim()) nextErrors.lastName = "Enter a last name.";
    if (!form.address.trim()) nextErrors.address = "Enter a street address.";
    if (!form.city.trim()) nextErrors.city = "Enter a city.";
    if (!form.region.trim()) nextErrors.region = "Enter a state or region.";
    if (!form.postalCode.trim()) {
      nextErrors.postalCode = "Enter a ZIP or postal code.";
    } else if (form.postalCode.trim().length < 3) {
      nextErrors.postalCode = "Enter a valid ZIP or postal code.";
    }
    if (!form.country) nextErrors.country = "Choose a country.";

    return nextErrors;
  };

  const placeOrder = (event) => {
    event.preventDefault();
    if (!lineItems.length || isSubmitting) return;

    const nextErrors = validate();
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      const firstInvalidField = Object.keys(nextErrors)[0];
      window.requestAnimationFrame(() => {
        fieldRefs.current[firstInvalidField]?.focus();
      });
      return;
    }

    setIsSubmitting(true);
    const randomPart = window.crypto?.getRandomValues
      ? Array.from(window.crypto.getRandomValues(new Uint32Array(2))).join("")
      : String(Math.floor(Math.random() * 1e16));
    const orderNo = `${Date.now()}${randomPart}`.slice(0, 28);
    const paymentUrl = new URL("/create_payment.php", window.location.origin);
    const successUrl = new URL("/payment", window.location.origin);
    successUrl.searchParams.set("state", "success");
    const cancelUrl = new URL("/payment", window.location.origin);
    cancelUrl.searchParams.set("state", "cancel");
    const estimatedDelivery = new Date();
    estimatedDelivery.setDate(estimatedDelivery.getDate() + 5);
    try {
      window.sessionStorage.setItem(
        "storefront:lastOrder",
        JSON.stringify({
          number: orderNo,
          placedAt: new Date().toISOString(),
          estimatedDelivery: estimatedDelivery.toISOString(),
          itemCount: lineItems.reduce((sum, line) => sum + line.quantity, 0),
          shipping,
          discount,
          total,
        })
      );
    } catch {
      // Payment can continue when browser storage is unavailable.
    }
    paymentUrl.search = new URLSearchParams({
      orderNo,
      amount: total.toFixed(2),
      currency: siteConfig.currency || "USD",
      email: form.email.trim(),
      success_url: successUrl.toString(),
      cancel_url: cancelUrl.toString(),
      title: lineItems[0].product.name,
    }).toString();
    window.location.assign(paymentUrl.toString());
  };

  if (!lineItems.length) {
    return (
      <main className="checkout-page checkout-page--empty">
        <section
          className="checkout-empty"
          aria-labelledby="checkout-empty-title"
        >
          <span className="checkout-eyebrow">Checkout</span>
          <h1 id="checkout-empty-title">Your cart is empty.</h1>
          <p>
            Add something delicious from {siteConfig.brandName} before starting
            checkout.
          </p>
          <div className="checkout-empty__actions">
            <Link
              className="checkout-button checkout-button--primary"
              to="/shop"
            >
              Browse products
            </Link>
            <Link className="checkout-text-link" to="/cartpage">
              Return to cart
            </Link>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="checkout-page">
      <div className="checkout-shell">
        <header className="checkout-heading">
          <Link className="checkout-heading__back" to="/cartpage">
            <span aria-hidden="true">←</span> Back to cart
          </Link>
          <span className="checkout-eyebrow">Secure checkout</span>
          <h1>Where should we send the good stuff?</h1>
          <p>
            Enter your contact and delivery details, then continue to secure
            payment.
          </p>
        </header>

        <div className="checkout-layout">
          <form className="checkout-form" onSubmit={placeOrder} noValidate>
            {Object.values(errors).some(Boolean) && (
              <div className="checkout-error-summary" role="alert">
                Please review the highlighted fields before placing your order.
              </div>
            )}

            <section
              className="checkout-section"
              aria-labelledby="contact-heading"
            >
              <div className="checkout-section__heading">
                <span>1</span>
                <div>
                  <h2 id="contact-heading">Contact</h2>
                  <p>We use this email for payment and order updates.</p>
                </div>
              </div>
              <div className="checkout-field">
                <label htmlFor="checkout-email">Email address</label>
                <input
                  ref={(node) => {
                    fieldRefs.current.email = node;
                  }}
                  id="checkout-email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={updateField}
                  autoComplete="email"
                  inputMode="email"
                  aria-invalid={Boolean(errors.email)}
                  aria-describedby={
                    errors.email ? "checkout-email-error" : undefined
                  }
                />
                {errors.email && (
                  <p
                    className="checkout-field__error"
                    id="checkout-email-error"
                  >
                    {errors.email}
                  </p>
                )}
              </div>
            </section>

            <section
              className="checkout-section"
              aria-labelledby="delivery-heading"
            >
              <div className="checkout-section__heading">
                <span>2</span>
                <div>
                  <h2 id="delivery-heading">Delivery</h2>
                  <p>
                    Standard delivery typically arrives in 3–5 business days.
                  </p>
                </div>
              </div>

              <div className="checkout-field">
                <label htmlFor="checkout-country">Country / region</label>
                <select
                  ref={(node) => {
                    fieldRefs.current.country = node;
                  }}
                  id="checkout-country"
                  name="country"
                  value={form.country}
                  onChange={updateField}
                  autoComplete="country"
                  aria-invalid={Boolean(errors.country)}
                >
                  {DELIVERY_COUNTRIES.map(([code, label]) => (
                    <option key={code} value={code}>{label}</option>
                  ))}
                </select>
              </div>

              <div className="checkout-fields checkout-fields--two">
                <div className="checkout-field">
                  <label htmlFor="checkout-first-name">First name</label>
                  <input
                    ref={(node) => {
                      fieldRefs.current.firstName = node;
                    }}
                    id="checkout-first-name"
                    name="firstName"
                    type="text"
                    value={form.firstName}
                    onChange={updateField}
                    autoComplete="given-name"
                    aria-invalid={Boolean(errors.firstName)}
                    aria-describedby={
                      errors.firstName ? "checkout-first-name-error" : undefined
                    }
                  />
                  {errors.firstName && (
                    <p
                      className="checkout-field__error"
                      id="checkout-first-name-error"
                    >
                      {errors.firstName}
                    </p>
                  )}
                </div>
                <div className="checkout-field">
                  <label htmlFor="checkout-last-name">Last name</label>
                  <input
                    ref={(node) => {
                      fieldRefs.current.lastName = node;
                    }}
                    id="checkout-last-name"
                    name="lastName"
                    type="text"
                    value={form.lastName}
                    onChange={updateField}
                    autoComplete="family-name"
                    aria-invalid={Boolean(errors.lastName)}
                    aria-describedby={
                      errors.lastName ? "checkout-last-name-error" : undefined
                    }
                  />
                  {errors.lastName && (
                    <p
                      className="checkout-field__error"
                      id="checkout-last-name-error"
                    >
                      {errors.lastName}
                    </p>
                  )}
                </div>
              </div>

              <div className="checkout-field">
                <label htmlFor="checkout-company">
                  Company <span>(optional)</span>
                </label>
                <input
                  id="checkout-company"
                  name="company"
                  type="text"
                  value={form.company}
                  onChange={updateField}
                  autoComplete="organization"
                />
              </div>

              <div className="checkout-field">
                <label htmlFor="checkout-address">Street address</label>
                <input
                  ref={(node) => {
                    fieldRefs.current.address = node;
                  }}
                  id="checkout-address"
                  name="address"
                  type="text"
                  value={form.address}
                  onChange={updateField}
                  autoComplete="address-line1"
                  aria-invalid={Boolean(errors.address)}
                  aria-describedby={
                    errors.address ? "checkout-address-error" : undefined
                  }
                />
                {errors.address && (
                  <p
                    className="checkout-field__error"
                    id="checkout-address-error"
                  >
                    {errors.address}
                  </p>
                )}
              </div>

              <div className="checkout-field">
                <label htmlFor="checkout-apartment">
                  Apartment, suite, etc. <span>(optional)</span>
                </label>
                <input
                  id="checkout-apartment"
                  name="apartment"
                  type="text"
                  value={form.apartment}
                  onChange={updateField}
                  autoComplete="address-line2"
                />
              </div>

              <div className="checkout-fields checkout-fields--three">
                <div className="checkout-field">
                  <label htmlFor="checkout-city">City</label>
                  <input
                    ref={(node) => {
                      fieldRefs.current.city = node;
                    }}
                    id="checkout-city"
                    name="city"
                    type="text"
                    value={form.city}
                    onChange={updateField}
                    autoComplete="address-level2"
                    aria-invalid={Boolean(errors.city)}
                    aria-describedby={
                      errors.city ? "checkout-city-error" : undefined
                    }
                  />
                  {errors.city && (
                    <p
                      className="checkout-field__error"
                      id="checkout-city-error"
                    >
                      {errors.city}
                    </p>
                  )}
                </div>
                <div className="checkout-field">
                  <label htmlFor="checkout-region">State / region</label>
                  <input
                    ref={(node) => {
                      fieldRefs.current.region = node;
                    }}
                    id="checkout-region"
                    name="region"
                    type="text"
                    value={form.region}
                    onChange={updateField}
                    autoComplete="address-level1"
                    aria-invalid={Boolean(errors.region)}
                    aria-describedby={
                      errors.region ? "checkout-region-error" : undefined
                    }
                  />
                  {errors.region && (
                    <p
                      className="checkout-field__error"
                      id="checkout-region-error"
                    >
                      {errors.region}
                    </p>
                  )}
                </div>
                <div className="checkout-field">
                  <label htmlFor="checkout-postal">ZIP / postal code</label>
                  <input
                    ref={(node) => {
                      fieldRefs.current.postalCode = node;
                    }}
                    id="checkout-postal"
                    name="postalCode"
                    type="text"
                    value={form.postalCode}
                    onChange={updateField}
                    autoComplete="postal-code"
                    aria-invalid={Boolean(errors.postalCode)}
                    aria-describedby={
                      errors.postalCode ? "checkout-postal-error" : undefined
                    }
                  />
                  {errors.postalCode && (
                    <p
                      className="checkout-field__error"
                      id="checkout-postal-error"
                    >
                      {errors.postalCode}
                    </p>
                  )}
                </div>
              </div>
            </section>

            <button
              className="checkout-button checkout-button--primary checkout-submit"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? "Redirecting…"
                : `Pay now · ${money.format(total)}`}
            </button>
            <p className="checkout-form__legal">
              By continuing to payment, you agree to our{" "}
              <Link to="/terms">Terms</Link> and acknowledge our{" "}
              <Link to="/privacy">Privacy Policy</Link>.
            </p>
          </form>

          <aside
            className="checkout-summary"
            aria-labelledby="checkout-summary-title"
          >
            <h2 id="checkout-summary-title">Order summary</h2>

            <div className="checkout-products">
              {lineItems.map(({ item, product, quantity, lineTotal }) => (
                <article
                  className="checkout-product"
                  key={`${item.id}-${item.size || "default"}-${
                    item.color || "default"
                  }`}
                >
                  <div className="checkout-product__image-wrap">
                    <img
                      src={getImage(product)}
                      alt={product.name}
                      width="72"
                      height="72"
                      loading="lazy"
                      decoding="async"
                    />
                    <span aria-label={`Quantity ${quantity}`}>{quantity}</span>
                  </div>
                  <div className="checkout-product__details">
                    <h3>{product.name}</h3>
                    {getProductMeta(item, product) && (
                      <p>{getProductMeta(item, product)}</p>
                    )}
                  </div>
                  <strong>{money.format(lineTotal)}</strong>
                </article>
              ))}
            </div>

            <div className="checkout-promo">
              <label htmlFor="checkout-promo">Promo code</label>
              <div>
                <input
                  id="checkout-promo"
                  type="text"
                  value={promoInput}
                  onChange={(event) => {
                    setPromoInput(event.target.value);
                    setPromoMessage("");
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      applyPromo();
                    }
                  }}
                  placeholder="Enter code"
                  autoCapitalize="characters"
                  disabled={Boolean(appliedCode)}
                />
                {appliedCode ? (
                  <button type="button" onClick={removePromo}>
                    Remove
                  </button>
                ) : (
                  <button type="button" onClick={applyPromo}>
                    Apply
                  </button>
                )}
              </div>
              {promoMessage && (
                <p
                  className={
                    appliedCode
                      ? "checkout-promo__message checkout-promo__message--success"
                      : "checkout-promo__message"
                  }
                  role={appliedCode ? "status" : "alert"}
                >
                  {promoMessage}
                </p>
              )}
            </div>

            <dl className="checkout-totals">
              <div>
                <dt>Subtotal</dt>
                <dd>{money.format(subtotal)}</dd>
              </div>
              {discount > 0 && (
                <div className="checkout-totals__discount">
                  <dt>WELCOME10</dt>
                  <dd>−{money.format(discount)}</dd>
                </div>
              )}
              <div>
                <dt>Standard shipping</dt>
                <dd>{money.format(shipping)}</dd>
              </div>
              <div className="checkout-totals__grand-total">
                <dt>
                  Total <small>USD</small>
                </dt>
                <dd>{money.format(total)}</dd>
              </div>
            </dl>

            <div className="checkout-summary__assurance">
              <span aria-hidden="true">✓</span>
              <p>
                <strong>Fresh-arrival promise</strong>
                Carefully packed and ready to enjoy.
              </p>
            </div>
            <Link className="checkout-summary__policy" to="/shipping">
              View shipping information
            </Link>
          </aside>
        </div>
      </div>
    </main>
  );
};

export default CheckOut;
